import {
  CreateButton,
  DeleteButton,
  ExportButton,
  List,
  SaveButton,
  TextField,
  getDefaultSortOrder,
  useTable,
} from "@refinedev/antd";

// It is recommended to use explicit import as seen below to reduce bundle size.
// import { IconName } from "@ant-design/icons";
import * as Icons from "@ant-design/icons";

import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  FormProps,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import { CrudFilters, IResourceComponentsProps, getDefaultFilter, useModal } from "@refinedev/core";

import { IDigitalPublication } from "interfaces";
import prettyBytes from "pretty-bytes";
import React from "react";

export const DigitalPublicationList: React.FC<
  IResourceComponentsProps
> = () => {
  const [record, setRecord] = React.useState<IDigitalPublication>();

  const { tableProps, sorters, tableQuery: { isLoading }, searchFormProps, filters } =
    useTable<IDigitalPublication>({
      resource: "digital-publication",
      syncWithLocation: false,

      onSearch: (params: any) => {
        const filters: CrudFilters = [];
        const { title, edpNumber } = params;

        filters.push({
          field: "title",
          operator: "contains",
          value: title ? title : undefined,
        });
        filters.push({
          field: "edpNumber",
          operator: "contains",
          value: edpNumber ? edpNumber : undefined,
        });

        return filters;
      },

      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ]
      }
    });
  const { show, visible, close } = useModal();

  const columns: ColumnsType<IDigitalPublication> = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 300,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("title", sorters),
      sorter: true,
    },
    {
      title: "EDP Number",
      dataIndex: "edpNumber",
      key: "edpNumber",
      width: 140,
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("edpNumber", sorters),
      sorter: true,
    },
    {
      title: "Current Edition",
      dataIndex: "currentEdition",
      key: "currentEdition",
      width: 140,
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("currentEdition", sorters),
      sorter: true,
    },
    {
      title: "File Size",
      dataIndex: "fileSize",
      key: "fileSize",
      width: 120,
      render: (value) => <TextField value={prettyBytes(value)} />,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<Icons.DownloadOutlined />}
            href={record?.storageUrl}
            download={true}
          />
          <Button
            size="small"
            icon={<Icons.FilePdfOutlined />}
            download={false}
            onClick={() => {
              setRecord(record);
              show();
            }}
          />
          <DeleteButton
            hideText
            size="small"
            recordItemId={record.id}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <List
        breadcrumb={false}
        headerProps={{
          // onBack: () => window.history.back(),
          title: "Digital Bookshelf",
          subTitle: "electronic digital publications",
          tags: isLoading ? (
            <Tag color="orange">loading</Tag>
          ) : (
            <Tag color="green">live</Tag>
          ),
          extra: (
            <React.Fragment>
              <Space>
                <CreateButton />
                <Button
                  size="middle"
                  icon={<Icons.FilePdfOutlined />}
                  target="_blank"
                  href={`${process.env.REACT_APP_GCS_DIGITAL_PUBLICATION_URL || 'https://storage.googleapis.com/emr_digital_publication_index_v1'}/10000%20eMariner%20Digital%20Publication%20Reference%20List.pdf`}
                  download={false}
                >
                  View Index
                </Button>
                <ExportButton hidden />
              </Space>
            </React.Fragment>
          ),
          ghost: true,
          footer: (
            <Divider plain>
              © {new Date().getFullYear()} e-Mariner NavSolutions
            </Divider>
          ),
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xl={24} lg={24} xs={24}>
            <Card type="inner" title={"Filter"}>
              <Filter formProps={searchFormProps} filters={filters || []} />
            </Card>
          </Col>
          <Col xl={24} xs={24}>
            <Table
              {...tableProps}
              columns={columns}
              rowKey="id"
              size="middle"
              scroll={{ x: 1200 }}
              sticky
              pagination={{
                ...tableProps.pagination,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
            />
          </Col>
        </Row>
      </List>
      <Modal
        centered
        closeIcon={<Icons.CloseSquareFilled />}
        visible={visible}
        onCancel={close}
        width="80%"
        footer={null}
      >
        <div>
          <embed
            src={record?.embedUrl}
            type="application/pdf"
            width="99%"
            height="700px"
          />
        </div>
      </Modal>
    </>
  );
};

const Filter: React.FC<{ formProps: FormProps; filters: CrudFilters }> = (
  props
) => {
  const { formProps, filters } = props;

  return (

    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        title: getDefaultFilter("title", filters),
        edpNumber: getDefaultFilter("edpNumber", filters),
      }}
    >
      <Row gutter={[16, 16]} align="middle" justify="space-between">
        <Col xs={24} sm={24} md={10}>
          <Form.Item label={"Title"} name="title">
            <Input placeholder="filter by title" allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={10}>
          <Form.Item label={"EDP Number"} name="edpNumber">
            <Input placeholder="filter by EDP number" allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={4}>
          <Form.Item style={{ marginBottom: 0 }}>
            <SaveButton onClick={formProps.form?.submit} size="large" block>
              Filter
            </SaveButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
